import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, RefreshCw } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Notice } from '@/components/ui/notice';
import { Tabs } from '@/components/ui/tabs';
import { TrackerOverview } from '@/components/tracking/TrackerOverview';
import { TrackerPeople } from '@/components/tracking/TrackerPeople';
import { TrackerScreenshots } from '@/components/tracking/TrackerScreenshots';
import { isTrackerSetupError, trackerErrorCode, TRACKER_KEY } from '@/hooks/tracking/trackerQuery';
import { useTrackerHealth } from '@/hooks/tracking/useTrackerHealth';
import { useTrackerStats } from '@/hooks/tracking/useTrackerStats';
import { useTrackerUsers } from '@/hooks/tracking/useTrackerUsers';
import { usePermission } from '@/hooks/usePermission';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';

type TabKey = 'overview' | 'people' | 'shots';

/** Service default the People tab falls back to before /health has answered. */
const FALLBACK_INTERVAL_SECS = 300;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'people', label: 'People' },
  { key: 'shots', label: 'Screenshots' },
];

/**
 * HRMS › Tracking — admin panel for the desktop screenshot tracker. The tracker is an
 * external service (own DB + R2 bucket); the PMS backend proxies it with the admin secret
 * held server-side, and PMS permissions decide who sees and changes what.
 */
export function TrackingPage() {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.TRACKING_MANAGE);

  const [tab, setTab] = useState<TabKey>('overview');
  const [live, setLive] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const health = useTrackerHealth();
  const configured = health.data?.configured !== false;
  const stats = useTrackerStats(live, configured);
  const users = useTrackerUsers(live, configured);

  const setupError = [stats.error, users.error].find(isTrackerSetupError);
  const showSetup = !configured || !!setupError;
  // Query errors are typed `unknown` app-wide (the query client registers no error type).
  const healthError: unknown = !showSetup ? health.error : null;
  const fetching = health.isFetching || stats.isFetching || users.isFetching;
  const userList = users.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: TRACKER_KEY });

  const openScreenshots = (userId: string) => {
    setSelectedUserId(userId);
    setTab('shots');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Tracking</h1>
          <p className="text-sm text-muted-foreground">
            Presence, capture settings and screenshots from the desktop tracker app
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setLive(!live)}
            title="Refresh presence and stats every 30 seconds"
            aria-pressed={live}
          >
            <span className={cn('h-2 w-2 rounded-full', live ? 'animate-pulse bg-emerald-500' : 'bg-muted-foreground/50')} />
            {live ? 'Live' : 'Paused'}
          </Button>
          <Button size="sm" variant="outline" onClick={refresh} disabled={fetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', fetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {showSetup && (
        <Notice
          tone="warning"
          icon={KeyRound}
          title={trackerErrorCode(setupError) === 'TRACKER_AUTH_FAILED'
            ? 'The tracker refused the admin secret'
            : 'The tracker is not configured'}
        >
          Set TRACKER_ADMIN_SECRET in the backend .env to the tracker server&apos;s admin secret, then restart
          the backend.
        </Notice>
      )}

      {health.data?.r2 === 'off' && (
        <Notice tone="warning" icon={AlertTriangle} compact>
          R2 storage is not configured on the tracker server, so screenshots are not being saved.
        </Notice>
      )}

      {healthError != null && (
        <Notice
          tone="danger"
          title="Could not reach the tracker"
          action={<Button size="sm" variant="outline" onClick={refresh}>Retry</Button>}
        >
          {getErrorMessage(healthError)}
        </Notice>
      )}

      {!showSetup && (
        <>
          <Tabs tabs={TABS} active={tab} onChange={(k) => setTab(k as TabKey)} />

          {tab === 'overview' && (
            <TrackerOverview
              stats={stats.data}
              users={userList}
              loading={stats.isLoading || users.isLoading}
              error={users.isError}
              onRetry={refresh}
              onOpenScreenshots={openScreenshots}
            />
          )}
          {tab === 'people' && (
            <TrackerPeople
              users={userList}
              loading={users.isLoading}
              error={users.isError}
              onRetry={() => users.refetch()}
              defaultIntervalSecs={health.data?.defaults.intervalSecs || FALLBACK_INTERVAL_SECS}
              canManage={canManage}
              onOpenScreenshots={openScreenshots}
            />
          )}
          {tab === 'shots' && (
            <TrackerScreenshots
              users={userList}
              userId={selectedUserId}
              onUserChange={setSelectedUserId}
              canManage={canManage}
            />
          )}
        </>
      )}
    </div>
  );
}
